import { ErrorCircle, Send, SendFilled } from '@ori-ui/icons';
import type { SyntheticEvent } from 'react';
import React, { useCallback, useState } from 'react';

import { getApiClient } from '../../../api/ClientProvider';
import { TokenProvider } from '../../../api/helpers/TokenProvider';
import { useAppContext } from '../../../contexts';
import { useTranslationByKey } from '../../../helpers/useTranslationByKeys';
import { TranslationKey } from '../../../models';
import { NotifyTypeEnum } from '../helpers/NotifyTypeEnum';
import { useClassNames } from './useClassNames';

const REGEX_EMAIL = /^(([^\s(),:;<>@\]]+(\.[^\s(),.:;<>@[\]]+)*)|(.+))@(([^\s(),:;<>@[\]]+\.)+[^\s(),:;<>@[\]]{2,})$/i;

export interface NotifyFormProps {
    currentType: string;
    handleOnChange: (event: React.FormEvent<HTMLInputElement>) => void;
    handleOnSubmit: () => void;
    inputData: string;
    productCode: string;
}

export const NotifyForm: React.FC<NotifyFormProps> = (props) => {
    const { tenant } = useAppContext();
    const t = useTranslationByKey();
    const { currentType, inputData, handleOnSubmit, handleOnChange, productCode }: NotifyFormProps = props;
    const [isActiveInput, setIsActiveInput] = useState(false);
    const isEmail: boolean = NotifyTypeEnum.email === currentType;
    const client = getApiClient();
    const isCorrectInputData: boolean = isEmail ? REGEX_EMAIL.test(inputData) : !!inputData.length;
    const classNames = useClassNames({
        isActiveInput,
        isCorrectInputData,
        inputData,
    });

    const handleOnFocus = useCallback((): void => {
        setIsActiveInput(!isActiveInput);
    }, [isActiveInput]);

    const handleOnClick = useCallback(
        async (event: SyntheticEvent): Promise<void> => {
            event.preventDefault();

            if (!isCorrectInputData) {
                return;
            }

            const customerId = await TokenProvider.getCustomerId();
            await client.createBackInStockNotificationSubscription(tenant, {
                body: {
                    customerId: Number(customerId),
                    notificationChannel: isEmail ? 'Email' : 'Sms',
                    itemCode: productCode,
                    [isEmail ? 'email' : 'telephoneNumber']: inputData,
                },
            });

            handleOnSubmit();
        },
        [client, handleOnSubmit, inputData, isCorrectInputData, isEmail, productCode, tenant],
    );

    return (
        <div className={classNames.root}>
            <form className={classNames.form}>
                <div className={classNames.formField}>
                    <label className={classNames.inputLabel}>
                        {isEmail ? t(TranslationKey.enterYourEmail) : t(TranslationKey.enterTelephoneNumber)}
                    </label>
                    <input
                        className={classNames.inputField}
                        type="text"
                        value={inputData}
                        onBlur={handleOnFocus}
                        onChange={handleOnChange}
                        onFocus={handleOnFocus}
                    />
                    <div className={classNames.information}>
                        <ErrorCircle fontSize="inherit" />
                    </div>
                    <button className={classNames.button} type="submit" onClick={handleOnClick}>
                        {isCorrectInputData ? <SendFilled fontSize="inherit" /> : <Send fontSize="inherit" />}
                    </button>
                </div>
                {!isCorrectInputData && <p className={classNames.validateInfo}>{t(TranslationKey.invalidEmail)}</p>}
            </form>
        </div>
    );
};
